import { FormLabel, Skeleton, Typography, Stack, Box, Checkbox } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import React, { useMemo } from 'react';
import { FolderOutlined } from '@mui/icons-material';
import { useFolderSelectContext } from '@/context/folderSelectContext';
import { useGetDriveItemQuery, useGetFoldersAndFilesQuery } from '@/features/msGraph/msGraphApi';
import { getSharepointError } from '@/features/msGraph/helper';

const SharepointTreeItem = ({ item, disabled, index }) => {
	const {
		apiRef,
		expandedItems,
		selectedFolder,
		setSelectedFolder,
		cannotSelect: determineIfFolderSelectable,
	} = useFolderSelectContext();

	const expanded = useMemo(() => expandedItems.includes(item.id), [expandedItems]);

	const folderQuery = useGetFoldersAndFilesQuery(
		{
			itemId: item?.id,
			driveId: item?.parentReference?.driveId,
		},
		{ skip: !item.id || !item?.parentReference?.driveId || !expanded },
	);

	const folders = useMemo(() => {
		const { folders: newFolders } = folderQuery.data ?? { folders: [] };
		console.log('FOLDERS', folderQuery.data);
		return newFolders.map((f) => {
			const cannotSelect = determineIfFolderSelectable(f, index + 1);
			return {
				...f,
				isFolder: !Object.keys(f).includes('file'),
				cannotSelect,
			};
		});
	}, [folderQuery.data]);

	const generateLoadingSkeletons = () =>
		Array.from({ length: Math.min(item?.folder?.childCount ?? 1, 10) }, (x, i) => {
			return <Skeleton key={`loading-tree-item-${item.id}-${i}`} />;
		});

	const isDisabled = disabled || item.cannotSelect; // || selectedRows.some((r) => r.id === item.id);
	const checked = useMemo(() => selectedFolder == item.id, [selectedFolder]);

	const handleChange = (e) => {
		apiRef?.current?.focusItem(e, checked ? null : item.id);
		setSelectedFolder(checked ? null : item.id);
	};

	const itemName = () => {
		if ((item.name === 'root' || item.root) && item.webUrl) {
			const url = item.webUrl.split('/');
			try {
				return decodeURI(url[url.length - 1].normalize());
			} catch (e) {
				console.log('Error normalising url name', e);
				return url[url.length - 1];
			}
		}
		return item.name;
	};

	return (
		<TreeItem
			itemId={item.id}
			label={
				<Stack
					direction='row'
					alignItems={'center'}
					onClick={(e) => {
						if (!isDisabled) {
							handleChange(e);
						}
					}}
				>
					<Checkbox
						size={'small'}
						checked={checked}
						onChange={handleChange}
						disabled={isDisabled}
						icon={<FolderOutlined />}
						checkedIcon={<FolderIcon />}
						sx={{ p: '4px' }}
					/>
					<Typography {...(checked && { sx: { fontWeight: 'bold' } })}>
						{itemName()}
					</Typography>
				</Stack>
			}
		>
			{folderQuery.isLoading ? (
				generateLoadingSkeletons()
			) : folderQuery.isError ? (
				<Box pl={'3em'}>
					<Typography variant={'task_updated'} color='error'>
						{getSharepointError(folderQuery.error)?.errorMessage}
					</Typography>
				</Box>
			) : folders.length > 0 ? (
				folders.map((f, i) => (
					<SharepointTreeItem
						key={`sharepoint-tree-item-child-${item.id}-${i}`}
						item={f}
						index={index + 1}
					/>
				))
			) : (
				<Box pl={'3em'}>
					<Typography variant={'task_updated'}>No folders found</Typography>
				</Box>
			)}
		</TreeItem>
	);
};

const SharepointFolderTree = () => {
	const { expandedItems, setExpandedItems, itemId, driveId, disableParent } =
		useFolderSelectContext();

	const {
		data: driveItem,
		isLoading,
		error,
	} = useGetDriveItemQuery(
		{
			driveId,
			itemId,
		},
		{ skip: !itemId || !driveId },
	);

	const handleExpandedItemsChange = (event, itemIds) => {
		setExpandedItems(itemIds);
	};

	return (
		<Stack>
			<FormLabel>Select folder</FormLabel>
			<Box
				sx={{
					maxHeight: '20em',
					overflowY: 'auto',
					pt: '1em',
				}}
			>
				<SimpleTreeView
					disableSelection
					expandedItems={expandedItems}
					onExpandedItemsChange={handleExpandedItemsChange}
				>
					{isLoading ? (
						<Skeleton />
					) : driveItem ? (
						<SharepointTreeItem item={driveItem} disabled={disableParent} index={0} />
					) : (
						<Typography variant={'task_updated'} color='error'>
							{getSharepointError(error)?.errorMessage}
						</Typography>
					)}
				</SimpleTreeView>
			</Box>
		</Stack>
	);
};

export default SharepointFolderTree;
